// TanStack Query is used only for REST (dictionaries, me, mutations); dictionary contents are read
// from Dexie (docs/project.md §8).
import { MutationCache, QueryCache, QueryClient, queryOptions } from '@tanstack/react-query';
import { api } from '@/api/endpoints';
import { shouldRetry } from '@/api/client';
import { db } from '@/db/db';
import { reportError } from '@/lib/errors';

// networkMode 'always': with the default 'online' mode a query started while navigator.onLine is
// false is paused, not run, and its promise never settles — the route guards awaiting /me and the
// dictionary list would hang and the app would open to a blank screen offline. Here every fetch
// runs and fails fast; the callers fall back to the local copy (loadUser, the index route).
export const queryClient = new QueryClient({
  // Every failed query and mutation; lib/errors drops the API's own answers
  queryCache: new QueryCache({ onError: (e) => reportError(e) }),
  mutationCache: new MutationCache({ onError: (e) => reportError(e) }),
  defaultOptions: {
    queries: { retry: shouldRetry, staleTime: 30_000, refetchOnWindowFocus: true, networkMode: 'always' },
    mutations: { retry: shouldRetry, networkMode: 'always' },
  },
});

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: ({ signal }) => api.me.get(signal),
  retry: false,
  staleTime: 5 * 60_000,
});

export const dictionariesQuery = queryOptions({
  queryKey: ['dictionaries'],
  queryFn: async ({ signal }) => {
    const res = await api.dictionaries.list(signal);
    // The list is cached in Dexie so the switcher works offline
    await db.transaction('rw', db.dictionaries, async () => {
      await db.dictionaries.clear();
      await db.dictionaries.bulkPut(res.dictionaries);
    });
    return res.dictionaries;
  },
  // offline: serve the Dexie copy
  placeholderData: undefined,
});

export const sessionsQuery = queryOptions({
  queryKey: ['sessions'],
  queryFn: () => api.me.sessions(),
});

export const sharesQuery = (dictionaryId: string) =>
  queryOptions({
    queryKey: ['shares', dictionaryId],
    queryFn: () => api.dictionaries.shares(dictionaryId),
  });
